import { todayCalendarDate } from '@shared/dates/calendarDate';
import { enhancedApi } from '@store/generated/intake-hub';

function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const match = header.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  return match ? match[1].replace(/['"]/g, '').trim() : null;
}

async function archiveDownloadResponseHandler(
  response: Response,
  fallbackFilename: string,
): Promise<{ blob: Blob; filename: string }> {
  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(error?.message || 'Failed to download documents');
  }
  const blob = await response.blob();
  const filename =
    parseContentDispositionFilename(response.headers.get('Content-Disposition')) ||
    fallbackFilename;
  return { blob, filename };
}

const INTAKE_HUB_TAG_TYPES = [
  'IntakeHubDashboard',
  'IntakeHubDefinitionCatalog',
  'IntakeHubDocuments',
  'IntakeHubEnrollment',
  'IntakeHubOrgEnrollments',
  'IntakeHubConfig',
  'IntakeHubItem',
  'IntakeHubAggregate',
] as const;

/**
 * Verifying or discarding a verification changes the provider's journey status,
 * the organization roster status, and the org/clinic progress bar. The progress
 * bar is served by the aggregate cache, so it has to be invalidated alongside
 * both dashboard scopes or it keeps showing a stale value.
 */
export function intakeVerificationInvalidatedTags({
  organizationId,
  userId,
}: {
  organizationId: string;
  userId: string;
}) {
  return [
    { type: 'IntakeHubDashboard' as const, id: `${organizationId}:${userId}` },
    { type: 'IntakeHubDashboard' as const, id: `${organizationId}:all-users` },
    { type: 'IntakeHubAggregate' as const },
  ];
}

// Organization service selection uses the provider or staff organization
// endpoint. The legacy per-user enroll endpoints remain compatibility shims.
const enhancedIntakeHubApi = enhancedApi.enhanceEndpoints({
  addTagTypes: INTAKE_HUB_TAG_TYPES,
  endpoints: {
    getV2ProviderIntakeHubOrgServices: {
      providesTags: [{ type: 'IntakeHubOrgEnrollments', id: 'CURRENT' }],
    },
    putV2ProviderIntakeHubOrgServices: {
      invalidatesTags: (_result, error) =>
        error
          ? []
          : [
              { type: 'IntakeHubOrgEnrollments' },
              { type: 'IntakeHubDashboard' },
              { type: 'IntakeHubConfig' },
              { type: 'IntakeHubDocuments' },
              { type: 'IntakeHubAggregate' },
            ],
    },
    getV2AdminOrganizationsByOrganizationIdIntakeHubOrgEnrollments: {
      providesTags: (_result, _error, arg) => [
        { type: 'IntakeHubOrgEnrollments', id: arg.organizationId },
      ],
    },
    postV2AdminOrganizationsByOrganizationIdIntakeHubOrgEnrollments: {
      // The org POST fans out to every rostered provider, so unlike the
      // per-user enroll it can change any provider's dashboard. Invalidate the
      // whole tag type rather than the two scopes a per-user write touches.
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              { type: 'IntakeHubOrgEnrollments', id: arg.organizationId },
              { type: 'IntakeHubDashboard' },
              // Enrolling creates items, which is what the document inventory
              // links uploads to — the per-user flow refetched it by hand.
              { type: 'IntakeHubDocuments' },
              { type: 'IntakeHubAggregate' },
            ],
    },
    getV2AdminOrganizationsByOrganizationIdIntakeHubAggregate: {
      providesTags: (_result, _error, arg) => [
        { type: 'IntakeHubAggregate', id: `${arg.organizationId}:${arg.page ?? 1}` },
      ],
    },
    getV2AdminOrganizationsByOrganizationIdIntakeHubConfig: {
      providesTags: (_result, _error, arg) => [
        { type: 'IntakeHubConfig', id: `${arg.organizationId}:${arg.userId ?? 'all-users'}` },
      ],
    },
    putV2AdminOrganizationsByOrganizationIdIntakeHubConfig: {
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              { type: 'IntakeHubConfig', id: `${arg.organizationId}:${arg.userId ?? 'all-users'}` },
              { type: 'IntakeHubAggregate' },
              {
                type: 'IntakeHubDashboard',
                id: `${arg.organizationId}:${arg.userId ?? 'all-users'}`,
              },
              { type: 'IntakeHubDashboard', id: `${arg.organizationId}:all-users` },
            ],
    },
    getV2AdminIntakeHubDefinitions: {
      providesTags: [{ type: 'IntakeHubDefinitionCatalog', id: 'CATALOG' }],
    },
    putV2AdminIntakeHubDefinitionsServicesByServiceKey: {
      invalidatesTags: [{ type: 'IntakeHubDefinitionCatalog', id: 'CATALOG' }],
    },
    putV2AdminIntakeHubDefinitionsRequirementsByRequirementKey: {
      invalidatesTags: [{ type: 'IntakeHubDefinitionCatalog', id: 'CATALOG' }],
    },
    getV2AdminOrganizationsByOrganizationIdIntakeHubDashboard: {
      providesTags: (_result, _error, arg) => [
        {
          type: 'IntakeHubDashboard',
          id: `${arg.organizationId}:${arg.userId ?? 'all-users'}`,
        },
      ],
    },
    getV2AdminOrganizationsByOrganizationIdIntakeHubDocuments: {
      providesTags: (_result, _error, arg) => [
        {
          type: 'IntakeHubDocuments',
          id: `${arg.organizationId}:${arg.userId ?? 'all-users'}`,
        },
      ],
    },
    postV2AdminOrganizationsByOrganizationIdIntakeHubMaterialize: {
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'IntakeHubDashboard',
          id: `${arg.organizationId}:${arg.userId}`,
        },
        { type: 'IntakeHubDashboard', id: `${arg.organizationId}:all-users` },
        { type: 'IntakeHubAggregate' },
      ],
    },
    postV2AdminOrganizationsByOrganizationIdIntakeHubVerification: {
      invalidatesTags: (_result, _error, arg) =>
        intakeVerificationInvalidatedTags({
          organizationId: arg.organizationId,
          userId: arg.userId,
        }),
    },
    postV2AdminOrganizationsByOrganizationIdIntakeHubItemsAndIntakeItemIdDocumentReferences: {
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'IntakeHubDashboard',
          id: `${arg.organizationId}:${arg.userId ?? 'all-users'}`,
        },
        { type: 'IntakeHubDashboard', id: `${arg.organizationId}:all-users` },
        {
          type: 'IntakeHubDocuments',
          id: `${arg.organizationId}:${arg.userId ?? 'all-users'}`,
        },
        { type: 'IntakeHubDocuments', id: `${arg.organizationId}:all-users` },
        { type: 'IntakeHubItem', id: arg.intakeItemId },
        { type: 'IntakeHubAggregate' },
      ],
    },
    postV2AdminOrganizationsByOrganizationIdIntakeHubProviderRoster: {
      invalidatesTags: [{ type: 'IntakeHubDashboard' }, { type: 'IntakeHubAggregate' }],
    },
    deleteV2AdminOrganizationsByOrganizationIdIntakeHubProviderRosterAndUserId: {
      invalidatesTags: [{ type: 'IntakeHubDashboard' }, { type: 'IntakeHubAggregate' }],
    },
    patchV2AdminOrganizationsByOrganizationIdIntakeHubItemsAndIntakeItemIdEvidence: {
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'IntakeHubDashboard',
          id: `${arg.organizationId}:${arg.userId ?? 'all-users'}`,
        },
        { type: 'IntakeHubDashboard', id: `${arg.organizationId}:all-users` },
        {
          type: 'IntakeHubDocuments',
          id: `${arg.organizationId}:${arg.userId ?? 'all-users'}`,
        },
        { type: 'IntakeHubDocuments', id: `${arg.organizationId}:all-users` },
        { type: 'IntakeHubItem', id: arg.intakeItemId },
        { type: 'IntakeHubAggregate' },
      ],
    },
    putV2AdminOrganizationsByOrganizationIdIntakeHubItemsAndIntakeItemIdSensitiveFields: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'IntakeHubDashboard', id: `${arg.organizationId}:${arg.userId}` },
        { type: 'IntakeHubDashboard', id: `${arg.organizationId}:all-users` },
        { type: 'IntakeHubItem', id: arg.intakeItemId },
        { type: 'IntakeHubAggregate' },
      ],
    },
    getV2ProviderIntakeHubDashboard: {
      providesTags: [{ type: 'IntakeHubDashboard', id: 'CURRENT' }],
    },
    getV2ProviderIntakeHubConfig: {
      providesTags: [{ type: 'IntakeHubConfig', id: 'CURRENT' }],
    },
    putV2ProviderIntakeHubConfig: {
      invalidatesTags: [
        { type: 'IntakeHubConfig', id: 'CURRENT' },
        { type: 'IntakeHubDashboard', id: 'CURRENT' },
      ],
    },
    getV2ProviderIntakeHubDocuments: {
      providesTags: [{ type: 'IntakeHubDocuments', id: 'CURRENT' }],
    },
    putV2ProviderIntakeHubDocumentFieldSuggestions: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'IntakeHubDashboard', id: 'CURRENT' },
        { type: 'IntakeHubDocuments', id: 'CURRENT' },
        ...[
          ...new Set(
            arg.acceptDocumentFieldSuggestionsRequest.suggestions.map(
              suggestion => suggestion.intake_item_id,
            ),
          ),
        ].map(id => ({ type: 'IntakeHubItem' as const, id })),
      ],
    },
    postV2ProviderIntakeHubMaterialize: {
      invalidatesTags: [{ type: 'IntakeHubDashboard', id: 'CURRENT' }],
    },
    postV2ProviderIntakeHubProviderRoster: {
      invalidatesTags: [{ type: 'IntakeHubDashboard', id: 'CURRENT' }],
    },
    deleteV2ProviderIntakeHubProviderRosterByUserId: {
      invalidatesTags: [{ type: 'IntakeHubDashboard', id: 'CURRENT' }],
    },
    postV2ProviderIntakeHubItemsByIntakeItemIdDocumentReferences: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'IntakeHubDashboard', id: 'CURRENT' },
        { type: 'IntakeHubDocuments', id: 'CURRENT' },
        { type: 'IntakeHubItem', id: arg.intakeItemId },
      ],
    },
    postV2ProviderIntakeHubItemsByIntakeItemIdDocumentsAndDocumentReferenceIdExtractionsExtractionIdFieldsFieldIdAccept:
      {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'IntakeHubDashboard', id: 'CURRENT' },
          { type: 'IntakeHubDocuments', id: 'CURRENT' },
          { type: 'IntakeHubItem', id: arg.intakeItemId },
        ],
      },
    patchV2ProviderIntakeHubItemsByIntakeItemIdEvidence: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'IntakeHubDashboard', id: 'CURRENT' },
        { type: 'IntakeHubDocuments', id: 'CURRENT' },
        { type: 'IntakeHubItem', id: arg.intakeItemId },
      ],
    },
    putV2ProviderIntakeHubItemsByIntakeItemIdSensitiveFields: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'IntakeHubDashboard', id: 'CURRENT' },
        { type: 'IntakeHubItem', id: arg.intakeItemId },
      ],
    },
  },
});

export interface IntakeHubJourneyStatus {
  submitted_at?: string | null;
  verified_at?: string | null;
}

export const intakeHubApi = enhancedIntakeHubApi.injectEndpoints({
  endpoints: build => ({
    discardAdminIntakeHubVerification: build.mutation<
      IntakeHubJourneyStatus,
      { organizationId: string; userId: string }
    >({
      query: ({ organizationId, userId }) => ({
        url: `/v2/admin/organizations/${organizationId}/intake-hub/verification`,
        method: 'DELETE',
        params: { user_id: userId },
      }),
      invalidatesTags: (_result, _error, arg) => intakeVerificationInvalidatedTags(arg),
    }),
    downloadIntakeHubDocumentsArchive: build.mutation<{ blob: Blob; filename: string }, null>({
      query: () => ({
        url: '/v2/provider/intake-hub/documents/download',
        responseHandler: (response: Response) => {
          const today = todayCalendarDate('UTC').replace(/-/g, '');
          return archiveDownloadResponseHandler(response, `intake-hub-documents-${today}.zip`);
        },
      }),
    }),
    downloadAdminIntakeHubDocumentsArchive: build.mutation<
      { blob: Blob; filename: string },
      { organizationId: string; userId?: string | null }
    >({
      query: ({ organizationId, userId }) => ({
        url: `/v2/admin/organizations/${organizationId}/intake-hub/documents/download`,
        params: userId ? { user_id: userId } : undefined,
        responseHandler: (response: Response) => {
          const today = todayCalendarDate('UTC').replace(/-/g, '');
          return archiveDownloadResponseHandler(response, `intake-hub-documents-${today}.zip`);
        },
      }),
    }),
  }),
});

export const {
  useLazyGetV2ProviderIntakeHubAgreementsQuery: useLazyGetAgreementSetupQuery,
  useLazyGetV2ProviderIntakeHubAgreementsPreparationsByPreparationIdQuery:
    useLazyGetAgreementReviewQuery,
  useLazyGetV2ProviderIntakeHubAgreementsExecutionsByExecutionIdQuery:
    useLazyGetAgreementExecutionQuery,
  usePostV2ProviderIntakeHubAgreementsPreparationsMutation: usePrepareAgreementMutation,
  usePostV2ProviderIntakeHubAgreementsPreparationsByPreparationIdDocumentsAndDocumentKeySignMutation:
    useSignAgreementMutation,
  useLazyGetV2AdminOrganizationsByOrganizationIdIntakeHubAgreementsQuery:
    useLazyGetAdminAgreementSetupQuery,
  useLazyGetV2AdminOrganizationsByOrganizationIdIntakeHubAgreementsPreparationsAndPreparationIdQuery:
    useLazyGetAdminAgreementReviewQuery,
  useLazyGetV2AdminOrganizationsByOrganizationIdIntakeHubAgreementsExecutionsAndExecutionIdQuery:
    useLazyGetAdminAgreementExecutionQuery,
  useGetV2ProviderIntakeHubOrgServicesQuery: useGetIntakeOrgServicesQuery,
  usePutV2ProviderIntakeHubOrgServicesMutation: useSetIntakeOrgServicesMutation,
  useGetV2AdminOrganizationsByOrganizationIdIntakeHubAggregateQuery:
    useGetAdminIntakeHubAggregateQuery,
  useGetV2AdminOrganizationsByOrganizationIdIntakeHubOrgEnrollmentsQuery:
    useGetAdminIntakeHubOrgEnrollmentsQuery,
  usePostV2AdminOrganizationsByOrganizationIdIntakeHubOrgEnrollmentsMutation:
    useSetAdminIntakeHubOrgEnrollmentsMutation,
  useGetV2AdminOrganizationsByOrganizationIdIntakeHubConfigQuery:
    useGetAdminIntakeFormConfigurationQuery,
  usePutV2AdminOrganizationsByOrganizationIdIntakeHubConfigMutation:
    useUpdateAdminIntakeFormConfigurationMutation,
  useGetV2AdminIntakeHubDefinitionsQuery: useGetAdminIntakeHubDefinitionsQuery,
  usePutV2AdminIntakeHubDefinitionsServicesByServiceKeyMutation:
    useUpsertAdminIntakeHubServiceDefinitionMutation,
  usePutV2AdminIntakeHubDefinitionsRequirementsByRequirementKeyMutation:
    useUpsertAdminIntakeHubRequirementDefinitionMutation,
  useGetV2AdminOrganizationsByOrganizationIdIntakeHubDashboardQuery:
    useGetAdminIntakeHubDashboardQuery,
  usePostV2AdminOrganizationsByOrganizationIdIntakeHubFormMutation:
    useGenerateAdminIntakeFormMutation,
  useGetV2AdminOrganizationsByOrganizationIdIntakeHubFormPayersQuery:
    useGetAdminIntakeFormPayerOptionsQuery,
  useLazyGetV2AdminOrganizationsByOrganizationIdIntakeHubDashboardQuery:
    useLazyGetAdminIntakeHubDashboardQuery,
  useGetV2AdminOrganizationsByOrganizationIdIntakeHubDocumentsQuery:
    useGetAdminIntakeHubDocumentsQuery,
  usePostV2AdminOrganizationsByOrganizationIdIntakeHubMaterializeMutation:
    useMaterializeAdminIntakeHubItemsMutation,
  usePostV2AdminOrganizationsByOrganizationIdIntakeHubVerificationMutation:
    useVerifyAdminIntakeHubMutation,
  usePostV2AdminOrganizationsByOrganizationIdIntakeHubProviderRosterMutation:
    useAddAdminIntakeProviderRosterEntryMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdIntakeHubProviderRosterAndUserIdMutation:
    useRemoveAdminIntakeProviderRosterEntryMutation,
  usePostV2AdminOrganizationsByOrganizationIdIntakeHubItemsAndIntakeItemIdDocumentReferencesMutation:
    useAttachAdminIntakeHubDocumentReferenceMutation,
  usePatchV2AdminOrganizationsByOrganizationIdIntakeHubItemsAndIntakeItemIdEvidenceMutation:
    useUpdateAdminIntakeHubItemEvidenceMutation,
  usePostV2ProviderIntakeHubFormMutation: useGenerateIntakeFormMutation,
  useGetV2ProviderIntakeHubConfigQuery: useGetIntakeFormConfigurationQuery,
  usePutV2ProviderIntakeHubConfigMutation: useUpdateIntakeFormConfigurationMutation,
  useGetV2ProviderIntakeHubFormPayersQuery: useGetIntakeFormPayerOptionsQuery,
  useGetV2ProviderIntakeHubDashboardQuery: useGetIntakeHubDashboardQuery,
  useLazyGetV2ProviderIntakeHubDashboardQuery: useLazyGetIntakeHubDashboardQuery,
  useGetV2ProviderIntakeHubDocumentsQuery: useGetIntakeHubDocumentsQuery,
  usePutV2ProviderIntakeHubDocumentFieldSuggestionsMutation:
    useAcceptIntakeHubDocumentFieldSuggestionsMutation,
  usePostV2ProviderIntakeHubMaterializeMutation: useMaterializeIntakeHubItemsMutation,
  usePostV2ProviderIntakeHubProviderRosterMutation: useAddIntakeProviderRosterEntryMutation,
  useDeleteV2ProviderIntakeHubProviderRosterByUserIdMutation:
    useRemoveIntakeProviderRosterEntryMutation,
  usePostV2ProviderIntakeHubItemsByIntakeItemIdDocumentReferencesMutation:
    useAttachIntakeHubDocumentReferenceMutation,
  usePostV2ProviderIntakeHubItemsByIntakeItemIdDocumentsAndDocumentReferenceIdExtractionsExtractionIdFieldsFieldIdAcceptMutation:
    useAcceptIntakeHubDocumentFieldSuggestionMutation,
  usePatchV2ProviderIntakeHubItemsByIntakeItemIdEvidenceMutation:
    useUpdateIntakeHubItemEvidenceMutation,
  usePutV2ProviderIntakeHubItemsByIntakeItemIdSensitiveFieldsMutation:
    useUpdateIntakeHubSensitiveFieldMutation,
  usePutV2AdminOrganizationsByOrganizationIdIntakeHubItemsAndIntakeItemIdSensitiveFieldsMutation:
    useUpdateAdminIntakeHubSensitiveFieldMutation,
  useDiscardAdminIntakeHubVerificationMutation,
  useDownloadAdminIntakeHubDocumentsArchiveMutation,
  useDownloadIntakeHubDocumentsArchiveMutation,
} = intakeHubApi;
